import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomLoader from '../../components/common/CustomLoader';
import { useNominationScope, useNominations } from './hooks/useNominations';
import { NominationFilters as NominationFiltersModel, SuccessNominationRecord } from './nomination.types';
import { ClientSuccessHistoryDialog } from './components/ClientSuccessHistoryDialog';
import { NominationCommentsDialog } from './components/NominationCommentsDialog';
import { NominationFilters } from './components/NominationFilters';
import { NominationStats } from './components/NominationStats';
import { NominationsTable } from './components/NominationsTable';
import {
  EMPTY_NOMINATION_FILTERS,
  buildLocationOptions,
  buildNominationStats,
  buildNominatorOptions,
  filterNominations,
} from './utils/nominationFilters';

const HIGHLIGHT_PARAM = 'highlight';
const SEARCH_PARAM = 'q';

/**
 * Manager/admin review page for Client Success nominations.
 * - Managers see nominations for clients at their assigned location; admins see all.
 * - Data is a realtime feed; filtering and sorting happen client-side.
 * - `?highlight=<id>` (from notifications) opens that nomination's comments.
 * - `?q=<text>` pre-fills the search box (used by the client page "Review all" link).
 */
const SuccessNominations: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = useNominationScope();
  const { nominations, isLoading, error, retry } = useNominations(scope);

  const [filters, setFilters] = useState<NominationFiltersModel>(() => ({
    ...EMPTY_NOMINATION_FILTERS,
    search: searchParams.get(SEARCH_PARAM) || '',
  }));
  const [commentsFor, setCommentsFor] = useState<SuccessNominationRecord | null>(null);
  const [historyFor, setHistoryFor] = useState<SuccessNominationRecord | null>(null);
  const [consumedHighlight, setConsumedHighlight] = useState<string | null>(null);

  const isAdmin = scope?.role === 'ADMIN';
  const isReviewer = scope?.role === 'ADMIN' || scope?.role === 'MANAGER';

  const filteredNominations = useMemo(() => filterNominations(nominations, filters), [nominations, filters]);
  const stats = useMemo(() => buildNominationStats(nominations), [nominations]);
  const nominatorOptions = useMemo(() => buildNominatorOptions(nominations), [nominations]);
  const locationOptions = useMemo(() => buildLocationOptions(nominations), [nominations]);

  // Open the highlighted nomination once it is present in the feed, then drop the param
  // so closing the dialog does not immediately reopen it.
  useEffect(() => {
    const highlightId = searchParams.get(HIGHLIGHT_PARAM);
    if (!highlightId || highlightId === consumedHighlight || nominations.length === 0) return;
    const match = nominations.find(nomination => nomination.id === highlightId);
    if (!match) return;
    setCommentsFor(match);
    setConsumedHighlight(highlightId);
    const next = new URLSearchParams(searchParams);
    next.delete(HIGHLIGHT_PARAM);
    setSearchParams(next, { replace: true });
  }, [nominations, searchParams, setSearchParams, consumedHighlight]);

  const openClientSuccess = useCallback(
    (clientId: string) => {
      if (!clientId) return;
      navigate(`/clients/${clientId}?tab=success`);
    },
    [navigate]
  );

  const handleRowClick = useCallback(
    (nomination: SuccessNominationRecord) => openClientSuccess(nomination.client_id),
    [openClientSuccess]
  );

  if (scope && !isReviewer) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error">You do not have permission to view Success Nominations.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1450, mx: 'auto' }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
            Success Nominations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            All submitted nominations from your team
          </Typography>
        </Box>

        <NominationStats stats={stats} />

        <Stack spacing={3}>
          <NominationFilters
            filters={filters}
            onChange={setFilters}
            nominatorOptions={nominatorOptions}
            locationOptions={locationOptions}
            showLocationFilter={Boolean(isAdmin)}
            resultCount={filteredNominations.length}
            totalCount={nominations.length}
          />

          <Card elevation={2}>
            <CardContent sx={{ p: 0 }}>
              {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
                  <CustomLoader />
                </Box>
              ) : error ? (
                <Alert
                  severity="error"
                  sx={{ m: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={retry}>
                      Retry
                    </Button>
                  }
                >
                  {error}
                </Alert>
              ) : nominations.length === 0 ? (
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center' }}>
                  <TrophyIcon sx={{ fontSize: 48, color: alpha(theme.palette.warning.main, 0.5), mb: 1.5 }} />
                  <Typography variant="h6" gutterBottom>
                    No nominations yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Staff can nominate a client from the Success tab on the client's page. New nominations appear
                    here instantly.
                  </Typography>
                </Paper>
              ) : (
                <NominationsTable
                  nominations={filteredNominations}
                  onRowClick={handleRowClick}
                  onOpenComments={setCommentsFor}
                  onOpenHistory={setHistoryFor}
                  showLocation={Boolean(isAdmin)}
                />
              )}
            </CardContent>
          </Card>
        </Stack>
      </motion.div>

      <NominationCommentsDialog
        open={Boolean(commentsFor)}
        nomination={commentsFor}
        onClose={() => setCommentsFor(null)}
        onViewClient={openClientSuccess}
      />
      <ClientSuccessHistoryDialog
        open={Boolean(historyFor)}
        clientId={historyFor?.client_id ?? null}
        clientName={historyFor?.client_name ?? ''}
        onClose={() => setHistoryFor(null)}
        onViewClient={openClientSuccess}
      />
    </Box>
  );
};

export default SuccessNominations;
